import { Client } from "./_clientService";
import {
  QueueName,
  JobState,
  GetJobsParams,
  CleanQueueParams,
} from "types/queue";

const executeRequest = async (options: any) => {
  try {
    const resp = await new (Client as any)(options)[options.method]();
    return resp;
  } catch (error) {
    throw error;
  }
};

/**
 * Get statistics for all queues
 */
export async function getQueueStats() {
  return executeRequest({
    path: "/queue/stats",
    method: "get",
  });
}

/**
 * Get failed jobs across all queues
 */
export async function getFailedJobs(limit: number = 50) {
  const urlParams = new URLSearchParams({ limit: String(limit) }).toString();
  return executeRequest({
    path: `/queue/failed?${urlParams}`,
    method: "get",
  });
}

/**
 * Get pending retry jobs across all queues
 */
export async function getPendingJobs(limit: number = 50) {
  const urlParams = new URLSearchParams({ limit: String(limit) }).toString();
  return executeRequest({
    path: `/queue/pending?${urlParams}`,
    method: "get",
  });
}

/**
 * Get stats for a specific queue
 */
export async function getSingleQueueStats(queueName: QueueName) {
  return executeRequest({
    path: `/queue/${queueName}/stats`,
    method: "get",
  });
}

/**
 * List jobs from a specific queue by state
 */
export async function getQueueJobs(params: GetJobsParams) {
  const {
    queueName,
    state = "waiting",
    jobName,
    start = 0,
    limit = 20,
  } = params;

  const queryParams: Record<string, string> = {
    state,
    start: String(start),
    limit: String(limit),
  };

  if (jobName) {
    queryParams.jobName = jobName;
  }

  const urlParams = new URLSearchParams(queryParams).toString();
  return executeRequest({
    path: `/queue/${queueName}/jobs?${urlParams}`,
    method: "get",
  });
}

/**
 * Get details of a specific job
 */
export async function getJobDetail(queueName: QueueName, jobId: string) {
  return executeRequest({
    path: `/queue/${queueName}/jobs/${jobId}`,
    method: "get",
  });
}

/**
 * Retry a failed job
 */
export async function retryJob(queueName: QueueName, jobId: string) {
  return executeRequest({
    path: `/queue/${queueName}/jobs/${jobId}/retry`,
    method: "post",
  });
}

/**
 * Remove a job from the queue
 */
export async function removeJob(queueName: QueueName, jobId: string) {
  return executeRequest({
    path: `/queue/${queueName}/jobs/${jobId}`,
    method: "delete",
  });
}

/**
 * Clean old jobs from a queue
 */
export async function cleanQueue(params: CleanQueueParams) {
  const { queueName, state, gracePeriodMs = 3600000 } = params;
  return executeRequest({
    path: `/queue/${queueName}/clean`,
    method: "post",
    payload: { state, gracePeriodMs },
  });
}

/**
 * Pause a queue
 */
export async function pauseQueue(queueName: QueueName) {
  return executeRequest({
    path: `/queue/${queueName}/pause`,
    method: "post",
  });
}

/**
 * Resume a paused queue
 */
export async function resumeQueue(queueName: QueueName) {
  return executeRequest({
    path: `/queue/${queueName}/resume`,
    method: "post",
  });
}
